/**
 * The agent app's location addresses, both shapes (docs/agent-clean-addresses-plan.md, PR 1).
 *
 * Shardul, 22 and 23 Oct 26: the app's addresses become clean and
 * professional, /app/sharma-dental-pune/posts, with no /l/ and no UUID. Every
 * old address keeps working by moving to the new one.
 *
 *   /app/l/<location uuid>/<page>[/...]   the old shape ('id'), moved in the
 *                                         browser (AppLocationLegacy.tsx); a
 *                                         link the app writes names it only
 *                                         while the slug is not known yet
 *   /app/<slug>/<page>[/...]              the clean shape ('slug'), which every
 *                                         in-app link names since PR 3
 *                                         (appPagePath, appHomeViewHref)
 *
 * A plain module with no React, so every reader of the address (agentCaller,
 * the nav, the dock, the Home chip, the prefetch, support chat, the domain
 * steps page) parses it the same way through parseAppLocationPath.
 *
 * The browser never makes a slug, it only learns them: from gbp-agent-portal
 * app_overview (each location it lists carries its slug) and from
 * agent-action-respond app_address. They are held in memory only, never in
 * localStorage; a reload fills the map again from the overview the shell
 * reads anyway. The slug shell renders a page only once its slug is in the
 * map, so agentCallerFromPath answers synchronously, as it does for a UUID.
 *
 * Slugs are per person since PR 1b (Shardul, 23 Oct 26: "Each owner always
 * sees their own clean name, even if someone else connected the same
 * business"): /app/destiny-perfumes-pune names each owner's own copy. So the
 * map holds one signed-in person's slugs at a time (appSlugViewer), is
 * emptied when slugs for another person arrive, and is emptied on sign out or
 * when someone else signs in (clearAppCaches in appData.ts). The server
 * decides which location a slug names for a person, in one place
 * (agent_viewer_slug_resolve and agent_viewer_slugs_sync, migration
 * 20261101022700); this map only holds what it answered.
 *
 * The reserved words and the slug shape live in appSlugRules.js (plain JS, so
 * server.js and the build read the same list).
 *
 * The home's main views have their own address since PR 1c (Shardul, 23 Oct
 * 26: "/app/destiny-perfumes-pune/inbox, /posts, /reviews: cleaner, like
 * professional apps. Filters (status, dates) stay after a '?' so links still
 * open the exact view. Old ?section= links keep working by redirect."):
 *
 *   /app/<slug>/home        Today
 *   /app/<slug>/inbox       Needs you (?tab=needs, the default, or done)
 *   /app/<slug>/agent       Agent (?tab=live, upnext, runs or how)
 *   /app/<slug>/activity    Agent activity
 *   /app/<slug>/growth      Growth
 *   /app/<slug>/settings    Settings (?tab=agent, mode, content and the rest)
 *
 * The same words follow /app/l/<uuid>/ while an old address waits to move.
 * Each one is the home page itself (`page` reads 'home'), showing that view.
 * Tabs, filters, open items and overlays stay after the '?'. The words sit
 * under the location segment, so they never meet a first-level /app word.
 *
 * Every other page is written in the owner's words since PR 6 (Shardul, 23
 * Oct 26): /app/<slug>/posts, /posts/new, /growth/local-rank, /profile/history,
 * /customers, /alerts, /plan and the rest (APP_PAGE_WORDS below). The code
 * still knows each page by its key, the word its no-login twin
 * /agent/<page>/<token> keeps ('ideas' for /posts), so parseAppLocationPath
 * and agentPageOf answer the key for either words, and every builder takes
 * the key and writes the words. An old word keeps working for ever under both
 * shapes: the frame moves it to its new words, query and hash kept
 * (appAddressWithPageWords, AppSlugShell.tsx).
 */
import { isAgencyMode } from '../utils/agencyRouting';
import { APP_RESERVED_WORDS, APP_SLUG_RE, isAppSlug, isReservedAppWord } from './appSlugRules';

export { APP_RESERVED_WORDS, APP_SLUG_RE, isAppSlug, isReservedAppWord };

/** The home's views that have their own path word (Today is the home itself, /home). */
export const APP_HOME_SECTION_WORDS = ['inbox', 'agent', 'activity', 'growth', 'settings'] as const;

export type AppHomeSectionWord = (typeof APP_HOME_SECTION_WORDS)[number];

export function isAppHomeSectionWord(value: unknown): value is AppHomeSectionWord {
  return typeof value === 'string' && (APP_HOME_SECTION_WORDS as readonly string[]).includes(value);
}

/**
 * The home's views are written into the path. The one switch for undoing PR
 * 1c: false, in a one-line PR, and the views stay in ?section= again. A view
 * address opened meanwhile (/app/<slug>/inbox) still opens its view.
 */
export const HOME_SECTIONS_IN_PATH = true;

/**
 * True when this host writes the home's views into the path: Fly's own hosts,
 * with the switch on. An agency's white-label host keeps ?section= exactly as
 * before, and declares no view route.
 */
export function homeViewsInPath(): boolean {
  return HOME_SECTIONS_IN_PATH && !isAgencyMode();
}

/**
 * Old /app/l/<uuid> addresses move to their slug address. The one switch for
 * undoing it: false, in a one-line PR, and old addresses render in place again
 * (slug addresses opened meanwhile keep working).
 */
export const MOVE_OLD_APP_ADDRESSES = true;

/* ---------------------------------------------- page words (PR 6) */

/**
 * The words each location page is written with after the location, in the
 * owner's language (docs/agent-clean-addresses-plan.md, PR 6). Shardul, 23 Oct
 * 26, on the plan's list: "go ahead its okay from my side", with one change:
 * notifications is alerts, because activity is already the home's Agent
 * activity view (PR 1c).
 *
 *   page key (its /agent/<page>/<token> word)   ->  /app/<slug>/<words>
 *
 * The one table of old and new words: the builders below write it, the
 * readers read both, the routes declare both (AppLocationRoutes.tsx), and the
 * worker's clean links carry a copy (scripts/gbp-agent/app-link.ts, held to
 * this one by tests/agent/unit/clean-app-links.test.ts), and so do the edge
 * functions' (supabase/functions/_shared/app-page-words.ts, held by
 * tests/agent/unit/edge-page-words.test.ts; clean addresses PR 9). pages.ts
 * exports it beside APP_LOCATION_PAGES, and a guard test fails when a page
 * has no words.
 *
 * Two keys may share words, and the first one listed is the page they open:
 * createpost and the older new-post are one page; dashboard goes to the home;
 * subscriptions and subscription are both the plan, which opens the
 * subscriptions page (the one that prices in the visitor's currency).
 *
 * The words never clash with the home's views (inbox, agent, activity,
 * growth, settings) except as their sub pages: growth/local-rank,
 * growth/performance, agent/how-it-works and settings/content sit under them,
 * so /app/<slug>/growth and /app/<slug>/agent still open the home's views.
 */
export const APP_PAGE_WORDS: Readonly<Record<string, string>> = Object.freeze({
  home: 'home',
  dashboard: 'home',
  ideas: 'posts',
  createpost: 'posts/new',
  'new-post': 'posts/new',
  templates: 'posts/frames',
  references: 'posts/reference-photos',
  keywords: 'growth/local-rank',
  performance: 'growth/performance',
  reports: 'reports',
  profile: 'profile',
  'gbp-categories': 'profile/categories',
  'gbp-services': 'profile/services',
  'gbp-attributes': 'profile/attributes',
  photos: 'profile/photos',
  'profile-changes': 'profile/history',
  'audit-report': 'profile/audit',
  // Not on the plan's list: Directory listings keeps its own word (no menu leads there since 12 Oct 26).
  listings: 'listings',
  website: 'website',
  blogs: 'blogs',
  'microsite-setup': 'website/setup',
  'review-generator-setup': 'reviews/get-more',
  'review-responder-setup': 'reviews/replies',
  contacts: 'customers',
  leads: 'customers/leads',
  offer: 'customers/offer',
  notifications: 'alerts',
  activities: 'agent/how-it-works',
  'content-settings': 'settings/content',
  subscriptions: 'plan',
  subscription: 'plan',
});

/**
 * Pages are written in their words (PR 6). The one switch for undoing it:
 * false, in a one-line PR, and every link names the page's key again and old
 * words stop moving. An address in the new words opened meanwhile still opens
 * its page (its routes stay declared).
 */
export const PAGE_WORDS_IN_PATH = true;

/**
 * True when this host writes and moves to the page words: Fly's own hosts,
 * with the switch on. An agency's white-label host keeps every page at its
 * key, /app/l/<uuid>/ideas, exactly as before, and declares no word route.
 */
export function pageWordsInPath(): boolean {
  return PAGE_WORDS_IN_PATH && !isAgencyMode();
}

const hasOwn = (o: object, k: string): boolean => Object.prototype.hasOwnProperty.call(o, k);

/** Each set of words once, in the table's order, with the page it opens: the first key listed with them. */
const PAGE_OF_WORDS = new Map<string, string>();
for (const [page, words] of Object.entries(APP_PAGE_WORDS)) if (!PAGE_OF_WORDS.has(words)) PAGE_OF_WORDS.set(words, page);

/** Every set of page words and the page it opens, each once, in the table's order: what the routes declare. */
export const APP_PAGE_WORD_ROUTES: ReadonlyArray<{ readonly words: string; readonly page: string }> = Object.freeze(
  [...PAGE_OF_WORDS].map(([words, page]) => Object.freeze({ words, page })),
);

/** The same, longest words first, so /profile/history is read before /profile. */
const PAGE_BY_WORDS: ReadonlyArray<readonly [string, string]> = [...PAGE_OF_WORDS].sort(
  (a, b) => b[0].split('/').length - a[0].split('/').length,
);

/** The words a page is written with in a link the app writes: its words on Fly's own hosts (PR 6), its key elsewhere. */
export function appPageWords(page: string): string {
  if (!pageWordsInPath() || !hasOwn(APP_PAGE_WORDS, page)) return page;
  return APP_PAGE_WORDS[page];
}

const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

export function isLocationUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID_RE.test(value);
}

export interface AppLocationAddress {
  shape: 'id' | 'slug';
  /**
   * The location the address names: the segment itself on the old shape (a
   * UUID on every real address; callers that need one check isLocationUuid),
   * the id the slug map holds on the clean shape, null while it is unknown.
   */
  locationId: string | null;
  /** The slug on the clean shape; null on the old one. */
  slug: string | null;
  /**
   * The page by its key, whichever words the address is written with: 'ideas' for /posts and
   * for the old /ideas (PR 6). '' on a bare location address, 'home' on a view of the home ('/inbox').
   */
  page: string;
  /** The home view the path names ('inbox' for /app/<slug>/inbox), null anywhere else (the home itself included). */
  section: AppHomeSectionWord | null;
  /** Everything after the location segment, as written: '' or a path starting with '/' ('/website/domain'). */
  rest: string;
  /** What follows the page's words, no trailing slash: '' or a step inside the page ('/domain' of /website/domain). */
  sub: string;
}

/* ------------------------------------------------------------ slug map */

const idBySlug = new Map<string, string>();
const slugById = new Map<string, string>();
/** Whose slugs the map holds (a user id), or null while it is empty or filled without one (tests). */
let viewer: string | null = null;

/** Slugs for another person empty the map first: a slug names a location only for the person it was answered to. */
function holdFor(forViewer: string | null | undefined): void {
  if (!forViewer || forViewer === viewer) return;
  if (viewer !== null || idBySlug.size > 0) {
    idBySlug.clear();
    slugById.clear();
  }
  viewer = forViewer;
}

/**
 * The slug and id of one location, as the server answered them for `forViewer`
 * (the signed-in user id; every app caller passes it). Anything malformed is ignored.
 */
export function rememberAppSlug(locationId: string | null | undefined, slug: string | null | undefined, forViewer?: string | null): void {
  if (!isLocationUuid(locationId) || !isAppSlug(slug)) return;
  holdFor(forViewer);
  const id = locationId.toLowerCase();
  const before = slugById.get(id);
  if (before && before !== slug) idBySlug.delete(before);
  // For one person a slug names one location: a slug moving to another location drops that one's pair.
  const holder = idBySlug.get(slug as string);
  if (holder && holder !== id) slugById.delete(holder);
  slugById.set(id, slug as string);
  idBySlug.set(slug as string, id);
}

/** Every location an overview lists that carries a slug, for the person it was answered to. */
export function rememberAppSlugs(
  locations: ReadonlyArray<{ location_id?: string | null; slug?: string | null }> | null | undefined,
  forViewer?: string | null,
): void {
  holdFor(forViewer);
  for (const loc of locations ?? []) rememberAppSlug(loc?.location_id, loc?.slug, forViewer);
}

/** Whose slugs the map holds, or null. */
export function appSlugViewer(): string | null {
  return viewer;
}

/** True when the map holds this signed-in person's slugs, so a slug in it may be trusted for them. */
export function appSlugsAreFor(user: string | null | undefined): boolean {
  return !!user && viewer === user;
}

/** The location a slug names, or null while this tab has not learned it. */
export function locationIdForSlug(slug: string | null | undefined): string | null {
  return slug ? idBySlug.get(slug) ?? null : null;
}

/** The slug of a location, or null while this tab has not learned it. */
export function slugForLocation(locationId: string | null | undefined): string | null {
  return locationId ? slugById.get(String(locationId).toLowerCase()) ?? null : null;
}

/** Sign out, or another person signed in (clearAppCaches): nothing of the last person's address book stays. Also a test seam. */
export function resetAppSlugs(): void {
  idBySlug.clear();
  slugById.clear();
  viewer = null;
}

/* ------------------------------------------------------------- parsing */

function stripQuery(pathname: string | null | undefined): string {
  return String(pathname ?? '').split('?')[0].split('#')[0];
}

/**
 * The page a location's sub path names, by its key, and what follows its words: a page's words
 * (/growth/local-rank is keywords, longest words first), else the first segment as written (an old
 * word is its own key: /ideas is ideas).
 */
function pageOf(rest: string): { page: string; sub: string } {
  const path = rest.replace(/\/+$/, '');
  for (const [words, page] of PAGE_BY_WORDS) {
    const at = `/${words}`;
    if (path === at || path.startsWith(`${at}/`)) return { page, sub: path.slice(at.length) };
  }
  const m = /^\/([^/]+)(.*)$/.exec(path);
  return m ? { page: m[1], sub: m[2] } : { page: '', sub: '' };
}

/** The page and the home view a location's sub path names: a lone view word is the home, showing that view. */
function viewOf(rest: string): { page: string; section: AppHomeSectionWord | null; sub: string } {
  const word = /^\/([^/]+)\/?$/.exec(rest)?.[1];
  if (isAppHomeSectionWord(word)) return { page: 'home', section: word, sub: '' };
  return { ...pageOf(rest), section: null };
}

/**
 * A location address in either shape, or null for every other path: /app
 * itself, the account pages (/app/billing and the other reserved words), an
 * /agent page, anything that is not a well formed slug. Query and hash are
 * ignored.
 */
export function parseAppLocationPath(pathname: string | null | undefined): AppLocationAddress | null {
  const clean = stripQuery(pathname);
  const m = /^\/app\/([^/]+)(\/.*)?$/.exec(clean);
  if (!m) return null;
  const first = m[1];
  const tail = m[2] ?? '';
  if (first === 'l') {
    const old = /^\/([^/]+)(\/.*)?$/.exec(tail);
    if (!old) return null;
    const rest = old[2] ?? '';
    return { shape: 'id', locationId: old[1], slug: null, ...viewOf(rest), rest };
  }
  if (!isAppSlug(first)) return null;
  return { shape: 'slug', locationId: locationIdForSlug(first), slug: first, ...viewOf(tail), rest: tail };
}

/** The location id an app address names, either shape, or null (unknown slug, not a location address). */
export function appLocationIdFromPath(pathname: string | null | undefined): string | null {
  return parseAppLocationPath(pathname)?.locationId ?? null;
}

/* ------------------------------------------------------------ builders */

/**
 * The same address with an old page word written in the page's words now (PR 6), on the same
 * location and in the same shape, with any step after the page, the query and the hash kept:
 *
 *   /app/<slug>/ideas?tab=calendar#x      ->  /app/<slug>/posts?tab=calendar#x
 *   /app/l/<uuid>/keywords                ->  /app/l/<uuid>/growth/local-rank
 *   /app/<slug>/dashboard                 ->  /app/<slug>/home
 *
 * Null when nothing moves: the words are already the page's (posts, website/domain, a view of the
 * home, a word the table does not hold), the path is not a location address (an unknown or upper
 * case slug, a reserved word, /app/billing), or this host keeps the keys (an agency's host, the
 * switch off).
 */
export function appAddressWithPageWords(path: string): string | null {
  if (!pageWordsInPath()) return null;
  const raw = String(path ?? '');
  const cut = raw.search(/[?#]/);
  const pathname = cut >= 0 ? raw.slice(0, cut) : raw;
  const parsed = parseAppLocationPath(pathname);
  if (!parsed || parsed.section) return null;
  const old = /^\/([^/]+)(.*)$/.exec(parsed.rest);
  if (!old || !hasOwn(APP_PAGE_WORDS, old[1]) || APP_PAGE_WORDS[old[1]] === old[1]) return null;
  const location = pathname.slice(0, pathname.length - parsed.rest.length);
  return `${location}/${APP_PAGE_WORDS[old[1]]}${old[2]}${cut >= 0 ? raw.slice(cut) : ''}`;
}

/**
 * The same address on the clean shape: the location segment replaced by the
 * slug, page and sub path kept, a bare location address opening the home.
 * Null when the path is not an old location address.
 */
export function slugAddressOf(pathname: string, slug: string): string | null {
  const parsed = parseAppLocationPath(pathname);
  if (!parsed || parsed.shape !== 'id' || !isAppSlug(slug)) return null;
  const rest = parsed.rest && parsed.rest !== '/' ? parsed.rest : '/home';
  return `/app/${slug}${rest}`;
}

/** A clean address with its slug replaced (an alias moving to the current slug), page, sub path kept. */
export function withSlug(pathname: string, slug: string): string | null {
  const parsed = parseAppLocationPath(pathname);
  if (!parsed || parsed.shape !== 'slug' || !isAppSlug(slug)) return null;
  const rest = parsed.rest && parsed.rest !== '/' ? parsed.rest : '/home';
  return `/app/${slug}${rest}`;
}

/**
 * The home's address showing one view, on the same location and in the same
 * shape: /app/<slug>/home with 'inbox' is /app/<slug>/inbox, and 'home' is
 * Today. Null when the path is not a location's home (or one of its views).
 */
export function appHomeViewPath(pathname: string, view: AppHomeSectionWord | 'home'): string | null {
  const parsed = parseAppLocationPath(pathname);
  if (!parsed || parsed.page !== 'home' || (view !== 'home' && !isAppHomeSectionWord(view))) return null;
  const location = parsed.shape === 'id' ? `l/${parsed.locationId}` : parsed.slug;
  return `/app/${location}/${view}`;
}

/* ------------------------------------------- the links the app writes (PR 3) */

/**
 * In-app links name the clean address straight away (PR 3), so a tap no
 * longer takes the old address's redirect hop. The one switch for undoing it:
 * false, in a one-line PR, and every link names /app/l/<uuid>/... again, which
 * still moves to the clean address when opened (MOVE_OLD_APP_ADDRESSES).
 */
export const APP_LINKS_USE_SLUGS = true;

/**
 * The location segment of a link the app writes: the signed-in person's slug
 * when this tab knows it, else l/<uuid>.
 *
 * Fly's own hosts only: an agency's white-label host declares no /app/:slug
 * route, so there every link stays /app/l/<uuid>/... exactly as before. And
 * only from a map that names its person (every app caller fills it with the
 * signed-in user id, and it is emptied when the person changes), because a
 * slug names a location only for the person it was answered to (PR 1b).
 */
function linkSegment(locationId: string): string {
  const slug = APP_LINKS_USE_SLUGS && viewer !== null && !isAgencyMode() ? slugForLocation(locationId) : null;
  return slug ? slug : `l/${locationId}`;
}

/**
 * A location page's address, for a link the app writes (the one builder every
 * in-app link goes through since PR 3): /app/<slug>/<page><tail> when this tab
 * knows the signed-in person's slug for the location, /app/l/<uuid>/<page><tail>
 * otherwise, which still moves to the clean address once opened. `page` is the
 * page's key ('ideas'), written in its words on Fly's own hosts (PR 6,
 * /app/<slug>/posts); a word the table does not hold (a view of the home) is
 * written as given. `tail` is everything after the page, kept as given: a sub
 * path ('/domain'), a query ('?tab=calendar'), an anchor.
 */
export function appPagePath(locationId: string, page = 'home', tail = ''): string {
  return `/app/${linkSegment(locationId)}/${appPageWords(page)}${tail}`;
}

/**
 * One of a location's home views, for a link the app writes. On Fly's own
 * hosts the view is its path word (PR 1c): /app/<slug>/inbox?tab=needs. On an
 * agency's host (or with HOME_SECTIONS_IN_PATH off) it is ?section= on the
 * home, exactly as before: /app/l/<uuid>/home?section=inbox&tab=needs.
 * 'home' is Today. `query` is the rest of the query (a tab, filters, an open
 * item), with or without its '?'.
 */
export function appHomeViewHref(locationId: string, view: AppHomeSectionWord | 'home', query?: string | null): string {
  const rest = String(query ?? '').replace(/^\?/, '');
  if (view !== 'home' && homeViewsInPath()) return appPagePath(locationId, view, rest ? `?${rest}` : '');
  const search = [view !== 'home' ? `section=${view}` : '', rest].filter(Boolean).join('&');
  return appPagePath(locationId, 'home', search ? `?${search}` : '');
}
